import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { formatQty, matchesSearchTerms, normalizeSearch, searchTerms, parseQty } from '@pos/core';
import { applyLocalChanges, deleteMeta, getMeta, setMeta, type CountDraft } from '@pos/local-db';
import { useSession } from '../../app/current.tsx';
import { db } from '../../lib/db.ts';
import { requestSync } from '../../services/sync.ts';
import { Alert, Button, Card, Input, PageHeader, Select, Spinner, cx, errorMessage } from '../../ui/index.tsx';
import { Dialog } from '../../ui/Dialog.tsx';
import { useCatalog, useMoney } from '../products/data.ts';
import { buildCountChanges } from './changes.ts';
import { formatStock, useStockContext, useStockMap, useStockRows, type StockRow } from './data.ts';

/** Productos que se agregan a la lista por tanda. */
const PAGE = 200;

type Entry = CountDraft['entries'][string];

const dateFormat = new Intl.DateTimeFormat('es-AR', { dateStyle: 'short', timeStyle: 'short' });

/** ¿Hubo movimientos de stock después de anotar lo contado? (Ventas, compras, otros ajustes.) */
const stockChanged = (entry: Entry | undefined, row: StockRow) => !!entry && entry.system !== row.qty;

/** Una fila: se vuelve a dibujar solo si cambia su producto o lo escrito en ella. */
const CountRow = memo(function CountRow({
  row: r,
  entry,
  onChange,
}: {
  row: StockRow;
  entry: Entry | undefined;
  onChange: (variantId: string, value: string, system: number) => void;
}) {
  const value = entry?.value ?? '';
  const parsed = value.trim() ? parseQty(value, r.item.product.unit_type) : null;
  const diff = parsed !== null ? parsed - r.qty : null;
  return (
    <div className={cx('flex items-center gap-3 p-3 sm:px-4', stockChanged(entry, r) && 'bg-amber-50')}>
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium">{r.label}</p>
        <p className="text-sm text-slate-500">
          Sistema: {formatStock(r)}
          {diff !== null && diff !== 0 && (
            <span className={cx('ml-2 font-medium', diff < 0 ? 'text-red-700' : 'text-emerald-700')}>
              {diff > 0 ? '+' : ''}
              {formatQty(diff, r.item.product.unit_type)}
            </span>
          )}
        </p>
        {parsed !== null && !r.tracked && <p className="text-xs text-slate-500">Al guardar, empieza a controlar su stock.</p>}
        {stockChanged(entry, r) && (
          <p className="text-xs text-amber-800">
            Hubo movimientos después de contarlo (el sistema tenía {formatQty(entry!.system, r.item.product.unit_type)}): volvé a contarlo.
          </p>
        )}
      </div>
      <div className="w-28 shrink-0">
        <Input
          inputMode="decimal"
          aria-label={`Contado de ${r.label}`}
          placeholder="Contado"
          className="text-right"
          invalid={!!value.trim() && parsed === null}
          value={value}
          onChange={(e) => onChange(r.variant.id, e.target.value, r.qty)}
        />
      </div>
    </div>
  );
});

/**
 * Toma de inventario: se anota lo que hay contado y el sistema genera los ajustes.
 * Solo cuentan los productos donde se escribió algo. Lo anotado se guarda solo en el dispositivo
 * (se puede salir y seguir después) hasta guardar los ajustes o descartar el conteo.
 */
export function CountPage() {
  const { writer, can } = useSession();
  const catalog = useCatalog();
  const stock = useStockMap();
  const rows = useStockRows(catalog, stock);
  const money = useMoney();
  const navigate = useNavigate();
  const context = useStockContext();
  const [category, setCategory] = useState('');
  const [query, setQuery] = useState('');
  const [counted, setCounted] = useState<CountDraft['entries']>({});
  const [startedAt, setStartedAt] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [review, setReview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const visible = useMemo(() => {
    const terms = searchTerms(query);
    return rows.filter(
      (r) =>
        r.item.product.active &&
        r.variant.active &&
        (!category || r.item.category?.id === category) &&
        matchesSearchTerms(`${r.item.search} ${normalizeSearch(r.label)}`, terms),
    );
  }, [rows, category, query]);

  // Se muestran TODOS los productos: los primeros enseguida y el resto a medida que se baja (de a
  // tandas, para que un catálogo grande no trabe el celular).
  const [shown, setShown] = useState(PAGE);
  const sentinel = useRef<HTMLDivElement>(null);
  useEffect(() => setShown(PAGE), [category, query]);
  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') return setShown(Number.MAX_SAFE_INTEGER);
    const io = new IntersectionObserver(([e]) => e?.isIntersecting && setShown((n) => n + PAGE), { rootMargin: '600px' });
    io.observe(el);
    return () => io.disconnect();
  }, [shown, visible.length]);
  // Conteo en curso: se recupera al entrar y se guarda en el dispositivo con cada cambio.
  useEffect(() => {
    let cancelled = false;
    void getMeta(db, 'count_draft')
      .then((draft) => {
        if (cancelled || !draft) return;
        setCounted(draft.entries);
        setStartedAt(draft.started_at);
      })
      .finally(() => !cancelled && setLoaded(true));
    return () => {
      cancelled = true;
    };
  }, []);
  useEffect(() => {
    if (!loaded) return;
    if (Object.keys(counted).length === 0) {
      void deleteMeta(db, 'count_draft');
      if (startedAt) setStartedAt(null);
    } else {
      void setMeta(db, 'count_draft', { started_at: startedAt ?? new Date().toISOString(), entries: counted });
    }
  }, [counted, startedAt, loaded]);

  const setOne = useCallback((variantId: string, value: string, system: number) => {
    setStartedAt((s) => s ?? new Date().toISOString());
    setCounted((c) => {
      const next = { ...c };
      if (value.trim() === '') delete next[variantId];
      else next[variantId] = { value, system };
      return next;
    });
  }, []);

  const entries = useMemo(
    () =>
      rows
        .filter((r) => (counted[r.variant.id]?.value ?? '').trim() !== '')
        .map((r) => ({
          row: r,
          counted: parseQty(counted[r.variant.id]!.value, r.item.product.unit_type),
          changed: stockChanged(counted[r.variant.id], r),
        })),
    [rows, counted],
  );
  const changedCount = entries.filter((e) => e.changed).length;
  const invalid = entries.filter((e) => e.counted === null);
  // Con diferencia, o sin control de stock (contarlo lo empieza a controlar, aunque coincida).
  const differences = entries.filter((e) => e.counted !== null && (e.counted !== e.row.qty || !e.row.tracked));
  const costImpact = differences.reduce(
    (sum, e) => sum + (e.row.cost === undefined ? 0 : Math.round(((e.counted! - e.row.qty) * e.row.cost) / 1000)),
    0,
  );

  if (!catalog || !stock || !loaded) {
    return (
      <div className="flex justify-center py-12 text-slate-500">
        <Spinner />
      </div>
    );
  }

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const { changes } = buildCountChanges(
        entries.map((e) => ({ variantId: e.row.variant.id, systemMilli: e.row.qty, countedMilli: e.counted!, tracked: e.row.tracked })),
        context(),
      );
      await applyLocalChanges(db, changes, writer);
      // El conteo ya se aplicó: se borra el guardado del dispositivo.
      await deleteMeta(db, 'count_draft');
      requestSync(0);
      navigate('/stock');
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-4 pb-24">
      <PageHeader
        title="Toma de inventario"
        description="Contá lo que hay y anotalo. Al guardar, el sistema ajusta las diferencias. Los productos que dejes en blanco no se tocan."
        actions={
          <Link to="/stock">
            <Button variant="secondary">Volver</Button>
          </Link>
        }
      />
      {entries.length > 0 && startedAt && (
        <Alert tone={changedCount > 0 ? 'warning' : 'info'}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span>
              <strong>Conteo en curso:</strong> {entries.length} {entries.length === 1 ? 'producto contado' : 'productos contados'}, empezado el{' '}
              {dateFormat.format(new Date(startedAt))}. Se guarda solo en este dispositivo: podés salir y seguir después.
              {changedCount > 0 &&
                ` ${changedCount} ${changedCount === 1 ? 'tuvo' : 'tuvieron'} movimientos después de contarlos (marcados en amarillo): volvé a contarlos antes de guardar.`}
            </span>
            <Button type="button" variant="ghost" onClick={() => setDiscarding(true)}>
              Descartar
            </Button>
          </div>
        </Alert>
      )}
      <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
        <Input type="search" placeholder="Buscar…" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Buscar producto" />
        <Select value={category} onChange={(e) => setCategory(e.target.value)} aria-label="Categoría">
          <option value="">Todas las categorías</option>
          {catalog.categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
      </div>

      <Card flush className="divide-y divide-slate-100">
        {visible.slice(0, shown).map((r) => (
          <CountRow key={r.variant.id} row={r} entry={counted[r.variant.id]} onChange={setOne} />
        ))}
        {visible.length === 0 && <p className="p-6 text-center text-sm text-slate-500">No hay productos con ese filtro.</p>}
        {shown < visible.length ? (
          <div ref={sentinel} className="flex items-center justify-center gap-2 p-3 text-sm text-slate-500">
            <Spinner className="size-4" /> Cargando más productos…
          </div>
        ) : (
          visible.length > 0 && (
            <p className="p-3 text-center text-sm text-slate-500">
              {visible.length} {visible.length === 1 ? 'producto' : 'productos'}
              {query || category ? ' con este filtro' : ' en total'}.
            </p>
          )
        )}
      </Card>

      <div className="pb-safe fixed inset-x-0 bottom-16 z-10 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur md:bottom-0 md:left-60">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-3">
          <p className="text-sm text-slate-600">
            {entries.length} contados · <strong>{differences.length}</strong> con diferencia
          </p>
          <Button onClick={() => setReview(true)} disabled={entries.length === 0 || invalid.length > 0}>
            Revisar y guardar
          </Button>
        </div>
      </div>

      <Dialog
        open={review}
        onClose={() => setReview(false)}
        title="Revisar la toma de inventario"
        footer={
          <>
            <Button variant="secondary" onClick={() => setReview(false)}>
              Seguir contando
            </Button>
            <Button onClick={save} loading={busy}>
              Guardar ajustes
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-sm">
          {differences.length === 0 ? (
            <p>Todo coincide con el sistema: no hay nada que ajustar.</p>
          ) : (
            <>
              <ul className="max-h-72 divide-y divide-slate-100 overflow-y-auto rounded-lg ring-1 ring-slate-200">
                {differences.map((e) => {
                  const diff = e.counted! - e.row.qty;
                  return (
                    <li key={e.row.variant.id} className={cx('flex justify-between gap-3 px-3 py-2', e.changed && 'bg-amber-50')}>
                      <span className="min-w-0 truncate">
                        {e.row.label}
                        {e.changed && <span className="block text-xs text-amber-800">Hubo movimientos después de contarlo</span>}
                        {!e.row.tracked && <span className="block text-xs text-slate-500">Empieza el control de stock</span>}
                      </span>
                      {diff === 0 ? (
                        <span className="shrink-0 font-medium text-slate-600">queda en {formatQty(e.counted!, e.row.item.product.unit_type)}</span>
                      ) : (
                        <span className={cx('shrink-0 font-medium', diff < 0 ? 'text-red-700' : 'text-emerald-700')}>
                          {diff > 0 ? '+' : ''}
                          {formatQty(diff, e.row.item.product.unit_type)}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
              {can('view_costs') && <p className="text-slate-600">Impacto a costo: {money(costImpact)}</p>}
            </>
          )}
          {changedCount > 0 && (
            <Alert tone="warning">
              {changedCount} {changedCount === 1 ? 'producto tuvo' : 'productos tuvieron'} movimientos (ventas, compras o ajustes) después de contarlos. Si
              los guardás así, el stock queda en lo que anotaste.
            </Alert>
          )}
          {error && <Alert tone="error">{error}</Alert>}
        </div>
      </Dialog>

      <Dialog
        open={discarding}
        onClose={() => setDiscarding(false)}
        title="¿Descartar el conteo?"
        footer={
          <>
            <Button variant="secondary" onClick={() => setDiscarding(false)}>
              Seguir contando
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                setCounted({});
                setDiscarding(false);
              }}
            >
              Descartar
            </Button>
          </>
        }
      >
        <p className="text-sm">
          Se borran los {entries.length} {entries.length === 1 ? 'producto anotado' : 'productos anotados'} y no se ajusta nada del stock.
        </p>
      </Dialog>
    </div>
  );
}
